import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { compose, includes, isEmpty } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';

import { codelistProvider, ComponentLoader } from 'components';
import PaginationRow from 'components/atoms/GefMui/PaginationRow';
import SortCell from 'components/atoms/GefMui/SortCell';
import TableHasNoResults from 'components/atoms/GefMui/TableHasNoResults';
import { codelists } from 'constants/codelists';
import { EMPTY_VALUES } from 'constants/general';
import fetch from 'features/common/fetch';
import { actions } from 'features/views/campaignManagement/contacts/actions';
import { ROLES_WITHOUT_RESULTS } from 'features/views/campaignManagement/contacts/constants';
import { getContacts, getSelectedContactIds, getSort } from 'features/views/campaignManagement/contacts/selectors';
import contactStates from 'features/views/campaignManagement/contactStates';
import user from 'features/views/user';

import ContactsTableRow from './TableRow';

const createColumns = (contactDataColumns) => {
	const fixedColumns = [
		['client', 'birthNumber'],
		['address'],
		['policyNo', 'status'],
		['policyAgent', 'assignedTo'],
	];

	const columnsWithoutPrint = contactDataColumns
		.filter((column) => column !== 'PRINT')
		.map((column) => {
			switch (column) {
				case 'POLICY_FINISH_DATE':
					return 'policyFinishDate';
				case 'PRODUCT':
					return 'product';
				case 'INSURANCE_PREMIUM':
					return 'insurancePremium';
				case 'IS_SELLER':
					return 'isSeller';
				default:
					console.error(`Unknown dynamic column ${column}`);
					return null;
			}
		})
		.filter((column) => !isNilOrEmpty(column));

	let dynamicColumns = [];
	for (let i = 0; i < columnsWithoutPrint.length; i += 2) {
		dynamicColumns = dynamicColumns.concat([columnsWithoutPrint.slice(i, i + 2)]);
	}

	const printColumn = contactDataColumns.includes('PRINT') ? [['print']] : [];
	return fixedColumns.concat(dynamicColumns).concat(printColumn);
};

const headers = [
	'client',
	'address',
	'policyAgent',
	'status',
	'print',
	'isSeller',
	'policyFinishDate',
	'insurancePremium',
];
const headersSort = {
	client: 'orderingClientName',
	address: 'client.address.zipCode',
	policyAgent: ['policyAgentSeller.surname', 'policyAgentSeller.firstName'],
	status: 'solutionState',
	print: 'printTypeId',
	isSeller: 'isSeller',
	policyFinishDate: 'contractEndDate',
	insurancePremium: 'policy.insurancePremium',
};

export const ContactsTable = ({
	contactDataColumns = EMPTY_VALUES.ARRAY,
	solutionStateCodelist,
	idkRegionsCodelist,
	idkAgenciesCodelist,
}) => {
	const dispatch = useDispatch();

	const contacts = useSelector(getContacts);
	const fetchState = useSelector(fetch.selectors.getFetchState(fetch.constants.urls.contactList));
	const selectedContactIds = useSelector(getSelectedContactIds);
	const sort = useSelector(getSort);
	const currentRole = useSelector(user.selectors.getCurrentRole);
	const availableCount = useSelector(contactStates.selectors.getAvailableCount);

	const handleLoadMore = useCallback(() => {
		dispatch(actions.loadMore());
	}, [dispatch]);

	const handlePaginationClick = useCallback(
		(e, page) => {
			dispatch(actions.loadPage(page - 1));
		},
		[dispatch],
	);

	const handleReload = useCallback(() => {
		dispatch(actions.reload());
	}, [dispatch]);

	const handleSelect = useCallback(
		(id) => () => {
			dispatch(actions.toggleContact(id));
		},
		[dispatch],
	);

	const handleSort = useCallback(
		(sort) => {
			dispatch(actions.sortRecords(sort));
		},
		[dispatch],
	);

	const userIsRoleWithoutResults = useMemo(
		() => includes(currentRole, ROLES_WITHOUT_RESULTS) && availableCount > 0,
		[currentRole, availableCount],
	);

	const createHeaderColumn = (column, sort, hasRecords) => {
		return column.map((item, i) => {
			return headers.includes(item) ? (
				<SortCell key={i} hasRecords={hasRecords} sortBy={headersSort[item]} sort={sort} onSort={handleSort}>
					{getLabelMessage(`campaignManagement:${item}`)}
				</SortCell>
			) : (
				<div key={i}>{getLabelMessage(`campaignManagement:${item}`)}</div>
			);
		});
	};

	const hasRecords = !isEmpty(contacts);

	return (
		<ComponentLoader fetchStorePath={fetch.constants.urls.contactList} onReload={handleReload}>
			<TableContainer>
				<Table size="small">
					<TableHead>
						<TableRow>
							{createColumns(contactDataColumns).map((column, i) => (
								<TableCell key={i}>{createHeaderColumn(column, sort, hasRecords)}</TableCell>
							))}
							<TableCell />
						</TableRow>
					</TableHead>
					<TableBody>
						{!hasRecords ? (
							<TableHasNoResults
								fetchState={fetchState}
								noResultsText={getLabelMessage(
									userIsRoleWithoutResults ? 'common:tableBeforeSearch' : 'error:noResults',
								)}
							/>
						) : (
							contacts.map((contact) => (
								<TableRow
									key={contact.id}
									onClick={handleSelect(contact.id)}
									hover={!selectedContactIds.includes(contact.id)}
									sx={{
										backgroundColor: selectedContactIds.includes(contact.id)
											? 'primary.shades.8p'
											: 'default',
									}}
								>
									<ContactsTableRow
										contact={contact}
										columnsPositions={createColumns(contactDataColumns)}
										solutionStateCodelist={solutionStateCodelist}
										idkRegionsCodelist={idkRegionsCodelist}
										idkAgenciesCodelist={idkAgenciesCodelist}
									/>
								</TableRow>
							))
						)}
					</TableBody>
					<PaginationRow
						fetchStorePath={fetch.constants.urls.contactList}
						colSpan={100}
						onLoadNext={handleLoadMore}
						onPaginationClick={handlePaginationClick}
					/>
				</Table>
			</TableContainer>
		</ComponentLoader>
	);
};

ContactsTable.propTypes = {
	contactDataColumns: PropTypes.arrayOf(PropTypes.string),
	idkAgenciesCodelist: PropTypes.array,
	idkRegionsCodelist: PropTypes.array,
	solutionStateCodelist: PropTypes.array,
};

export default compose(
	codelistProvider(codelists.solutionState, 'solutionStateCodelist'),
	codelistProvider(codelists.idkRegions, 'idkRegionsCodelist'),
	codelistProvider(codelists.idkAgencies, 'idkAgenciesCodelist'),
)(ContactsTable);
